import React, { useState, useEffect } from 'react';
import { Typography, Paper, Box, makeStyles } from '@material-ui/core';
import { CheckCircle } from '@material-ui/icons';
import { NewAnnouncementBanner } from '@backstage-community/plugin-announcements';
import { BackstageTheme } from '@backstage/theme';

const useStyles = makeStyles((theme: BackstageTheme) => ({
  emptyState: {
    padding: theme.spacing(3),
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid ${theme.palette.success.light}`,
    boxShadow: '0 2px 12px rgba(76, 175, 80, 0.1)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
    },
  },
  successIcon: {
    fontSize: 48,
    color: theme.palette.success.main,
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    color: theme.palette.success.main,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
  },
  bannerContainer: {
    '& > div': {
      minHeight: 'auto',
    }
  }
}));

class AnnouncementErrorBoundary extends React.Component<
  { children: React.ReactNode; onError: () => void },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; onError: () => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch() {
    this.props.onError();
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

export const AnnouncementBannerWithEmptyState: React.FC = () => {
  const classes = useStyles();
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Observar errores en el DOM y elementos de anuncio
    const timer = setTimeout(() => {
      // Verificar errores 404 o de red
      const errorElements = document.querySelectorAll(
        '[class*="error"], [class*="Error"], .MuiAlert-colorError, [data-testid*="error"]'
      );
      
      const hasNetworkError = Array.from(errorElements).some(el => {
        const text = el.textContent?.toLowerCase() || '';
        return text.includes('404') || 
               text.includes('not found') || 
               text.includes('failed') ||
               text.includes('error');
      });

      // Verificar si hay banners de anuncio visibles
      const bannerElements = document.querySelectorAll(
        '[data-testid*="announcement"], .MuiAlert-root:not(.MuiAlert-colorError), .announcement-banner'
      );
      
      const hasValidBanner = Array.from(bannerElements).some(el => {
        const computedStyle = window.getComputedStyle(el as Element);
        const isVisible = computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden';
        const text = el.textContent?.toLowerCase() || '';
        const isNotError = !text.includes('404') && !text.includes('error') && !text.includes('failed');
        return isVisible && isNotError;
      });

      // Mostrar estado vacío si hay errores o no hay banners válidos
      if (hasNetworkError || !hasValidBanner || hasError) {
        setShowEmptyState(true);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [hasError]);

  const handleBannerError = () => {
    setHasError(true);
    setShowEmptyState(true);
  };

  // Estado vacío con mensaje positivo
  if (showEmptyState) {
    return (
      <Paper className={classes.emptyState} elevation={0}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <CheckCircle className={classes.successIcon} />
          <Typography variant="h6" className={classes.title}>
            You're up to date!
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            There are no new announcements at the moment. We'll notify you when there are important updates.
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Mostrar el banner normal envuelto en error boundary
  return (
    <div className={classes.bannerContainer}>
      <AnnouncementErrorBoundary onError={handleBannerError}>
        <NewAnnouncementBanner />
      </AnnouncementErrorBoundary>
    </div>
  );
};
